/**
 * PRP-WEB-1 W3.3/W3.4: the plan page's edit model. Pure TS (no React) so it is tested alone.
 *
 * Mirrors the phone Sketch screen (`frontend/components/sketch/SketchController.ts`): every
 * change is a `@sketch-core` action run through `History.run` on a clone of the plan, so a web
 * edit produces the same geometry as the same edit on the phone. Saving mirrors
 * `planStore.commitPlan`: a room is sent only when `roomToV2` of it differs from `roomToV2` of
 * the room as loaded (canonical JSON), so an opened-but-untouched room is never rewritten.
 *
 * Editable = a v2 room (`version: 2`) with a `room_id` (the marker the server writes through).
 * v1 rooms draw as today and are refused here (DD3: no conversion from the web); rooms with no
 * geometry are listed, not drawn. An action that would change a room that is not editable
 * (e.g. a shared wall dragging a v1 neighbour) is rolled back.
 */
import {
  History, asRectangle, findRoom, geometryVersionOf, lengthInfo, restore, roomToV2, snapshot, wallLength,
  type Plan, type Room, type RoomGeometryV2,
} from '@sketch-core';
import type { FieldNote } from '../../../types';
import { planFromNotes } from '../FloorPlanView';
import type { PlanRoomEdit } from '../../../lib/planApi';
import { formatFeetInches as fmt } from './feetInches';

export type RoomEditability =
  | {
    editable: true;
    /** The marker's stored `geometryVersion` (schema version, K12; the server writes max(stored, 2)). */
    baseVersion: number;
    /** The marker note's `updated_at` as loaded: the server's edit-race token (409 plan_changed). */
    baseUpdatedAt: string | null;
    /** The marker's stored geometry: keys the core does not model are written back from it. */
    stored: Record<string, unknown>;
  }
  | { editable: false; reason: string };

export interface LoadedEditPlan {
  plan: Plan;
  /** By room id. Every drawn room has an entry. */
  rooms: Record<string, RoomEditability>;
  /** Rooms with notes but no shape (listed under the plan, not editable). */
  undrawn: { id: string; name: string }[];
  banners: { room: string; text: string }[];
}

export const V1_REASON = 'Edit this room on the phone app first (it was captured before room shapes).';
const NO_MARKER_REASON = 'This room has no room marker, so it cannot be saved from the web.';
const NEWER_REASON = 'This room was saved by a newer app. Edit it on the phone.';
/** The geometry schema this web build writes (the core's GEOMETRY_VERSION, K12). */
const WEB_GEOMETRY_VERSION = 2;
/** The server's marker rule (`app/mcp/projects/store.py::is_marker`). */
const MARKER_TEXT = 'Room created';
const isMarker = (n: FieldNote) => n.type === 'text' && !!n.room_id && (n.content ?? '').trim() === MARKER_TEXT;

/**
 * The plan as the project page draws it (`planFromNotes`: newest geometry per room_id), plus
 * which rooms the web may edit and what each edit is based on. The base is the room's marker
 * note (newest "Room created" note per room_id, the note the server writes through), exactly
 * as the server picks it.
 */
export function loadEditPlan(notes: FieldNote[]): LoadedEditPlan | null {
  const marker = new Map<string, FieldNote>();
  const named = new Map<string, string>();
  for (const n of notes) {
    if (n.room_id && !named.has(n.room_id)) named.set(n.room_id, n.room_type || 'Room');
    if (!isMarker(n)) continue;
    const cur = marker.get(n.room_id!);
    if (!cur || (n.created_at ?? '') >= (cur.created_at ?? '')) marker.set(n.room_id!, n);
  }
  const data = planFromNotes(notes);
  const undrawn = [...named.entries()]
    .filter(([id]) => !data?.plan.rooms.some((r) => r.id === id))
    .map(([id, name]) => ({ id, name }));
  if (!data) return undrawn.length ? { plan: emptyPlan(), rooms: {}, undrawn, banners: [] } : null;
  const rooms: Record<string, RoomEditability> = {};
  for (const r of data.plan.rooms) {
    const n = marker.get(r.id);
    const g = n?.room_geometry as unknown as RoomGeometryV2 | undefined;
    if (!n) rooms[r.id] = { editable: false, reason: NO_MARKER_REASON };
    else if (!g || g.version !== 2) rooms[r.id] = { editable: false, reason: V1_REASON };
    else if (geometryVersionOf(g) > WEB_GEOMETRY_VERSION) rooms[r.id] = { editable: false, reason: NEWER_REASON };
    else {
      rooms[r.id] = {
        editable: true, baseVersion: geometryVersionOf(g), baseUpdatedAt: n.updated_at ?? null,
        stored: g as unknown as Record<string, unknown>,
      };
    }
  }
  return { plan: data.plan, rooms, undrawn, banners: data.banners };
}

function emptyPlan(): Plan {
  return { rooms: [], defaults: {} as Plan['defaults'], next: 1 };
}

/**
 * JSON with sorted keys (planStore's `canon`), so equal documents compare equal, and numbers at
 * the core's precision (1e-6 ft, `round6`): every core edit `tidy`s the WHOLE plan, which rounds
 * a neighbour's 1/3-ft coordinates without changing that room. Such a room is not sent.
 */
export function canon(v: unknown): string {
  return JSON.stringify(v, (_k, val) => {
    if (typeof val === 'number') return Math.round(val * 1e6) / 1e6;
    return val && typeof val === 'object' && !Array.isArray(val)
      ? Object.keys(val).sort().reduce((o: Record<string, unknown>, k) => { o[k] = (val as Record<string, unknown>)[k]; return o; }, {})
      : val;
  });
}

/** The v2 keys the sketch core writes (`roomToV2`); any other stored key is carried over as stored. */
const CORE_KEYS = new Set([
  'version', 'geometryVersion', 'name', 'level', 'ceilingFt', 'corners', 'measurements', 'openings',
  'ceilingFromDefault', 'banner', 'unplaced', 'subrooms', 'primary', 'second', 'walls', 'ceiling', 'checkDims', 'spans', 'attachedEdge',
]);

/**
 * The document the web sends for a room. The server REPLACES the stored geometry with it (as a
 * phone save does), so every stored key the core does not model is kept from the stored copy;
 * the core's own keys come from `roomToV2` (an absent one was removed by the edit, e.g. the
 * check-dimensions note after a measurement). `primary`/`second` are recomputed by `roomToV2`
 * when the outline changed, so the v1 export fallback never reads a stale size.
 */
export function roomDoc(r: Room, stored: Record<string, unknown> | undefined): RoomGeometryV2 {
  const doc = roomToV2(r);
  const extra = Object.fromEntries(Object.entries(stored ?? {}).filter(([k]) => !CORE_KEYS.has(k)));
  return { ...extra, ...doc } as RoomGeometryV2;
}

/** Server limits (`project_plan.py` PLAN_LIMITS), checked before an edit is kept. */
export const LIMITS = { wallMaxFt: 200, roomMinFt: 0.5, roomMaxFt: 200, ceilingMinFt: 3, ceilingMaxFt: 30, openingMinFt: 0.5, openingMaxFt: 200, fitTolFt: 1 / 12 };

/** Why the server would refuse this room, in the panel's words; null when it is fine. */
export function roomProblem(r: Room): string | null {
  const xs = r.corners.map((c) => c.x), ys = r.corners.map((c) => c.y);
  const w = Math.max(...xs) - Math.min(...xs), h = Math.max(...ys) - Math.min(...ys);
  if (Math.min(w, h) < LIMITS.roomMinFt) return `A room must be at least ${fmt(LIMITS.roomMinFt)} each way.`;
  if (Math.max(w, h) > LIMITS.roomMaxFt) return `A room can be at most ${LIMITS.roomMaxFt}' each way.`;
  const n = r.corners.length;
  const len = (vid: number) => {
    const i = r.corners.findIndex((c) => c.id === vid);
    if (i < 0) return null;
    const a = r.corners[i], b = r.corners[(i + 1) % n];
    return Math.hypot(b.x - a.x, b.y - a.y);
  };
  if (r.corners.some((c) => (len(c.id) ?? 0) > LIMITS.wallMaxFt)) return `A wall can be at most ${LIMITS.wallMaxFt}' long.`;
  if (r.ceilingFt < LIMITS.ceilingMinFt || r.ceilingFt > LIMITS.ceilingMaxFt) {
    return `Ceiling height must be between ${LIMITS.ceilingMinFt}' and ${LIMITS.ceilingMaxFt}'.`;
  }
  for (const o of r.openings) {
    if (o.widthFt < LIMITS.openingMinFt || o.widthFt > LIMITS.openingMaxFt) {
      return `An opening must be ${fmt(LIMITS.openingMinFt)} to ${LIMITS.openingMaxFt}' wide.`;
    }
    const L = len(o.edgeStart);
    if (L == null) return 'An opening is on a wall that is gone.';
    if (o.offFt < -LIMITS.fitTolFt || o.offFt + o.widthFt > L + LIMITS.fitTolFt) return 'An opening runs past the end of its wall.';
  }
  return null;
}

const docKey = (r: Room | undefined): string => (r ? canon(roomToV2(r)) : '');

/** Ids of rooms whose stored document would change (commitPlan's rule). */
export function changedRoomIds(before: Plan, after: Plan): string[] {
  const prev = new Map(before.rooms.map((r) => [r.id, r]));
  return after.rooms.filter((r) => docKey(prev.get(r.id)) !== docKey(r)).map((r) => r.id);
}

/**
 * The `PUT /plan` body rows: every changed editable room, and (PRP-WEB-2) every room made on
 * the web since the load (`create: true`, base 0 / null: the server makes its marker).
 */
export function savePayload(before: Plan, after: Plan, rooms: Record<string, RoomEditability>): PlanRoomEdit[] {
  return changedRoomIds(before, after).flatMap((id) => {
    const e = rooms[id];
    const r = findRoom(after, id);
    if (!r) return [];
    if (!e) return [{ room_id: id, geometry: roomDoc(r, undefined), base_geometry_version: 0, base_updated_at: null, create: true }];
    if (!e.editable) return [];
    return [{ room_id: id, geometry: roomDoc(r, e.stored), base_geometry_version: e.baseVersion, base_updated_at: e.baseUpdatedAt }];
  });
}

/** Rooms loaded from the server that the web deleted (only editable ones: `runEdit` refuses the rest). */
export function deletedRoomIds(before: Plan, after: Plan, rooms: Record<string, RoomEditability>): string[] {
  const now = new Set(after.rooms.map((r) => r.id));
  return before.rooms.filter((r) => !now.has(r.id) && rooms[r.id]?.editable).map((r) => r.id);
}

export type EditResult = { ok: true; note?: string } | { ok: false; message: string; options?: { label: string; moveQ: boolean }[] };

/**
 * Run one core action the way the phone controller does (`history.run(clone(plan), action)`),
 * refusing it when it would touch a room the web may not edit. Returns the new plan on ok.
 */
export function runEdit<T extends { ok: boolean; message?: string }>(
  plan: Plan, history: History, rooms: Record<string, RoomEditability>, action: (p: Plan) => T,
): { plan: Plan; res: T } | { plan: null; res: T | { ok: false; message: string } } {
  const next = restore(snapshot(plan));
  // `snapshot` drops nothing the drawing needs (rooms incl. damage, defaults, next).
  const res = history.run(next, action);
  if (!res.ok) return { plan: null, res };
  // A room new since the load (no entry) is the web's own; a room that is gone must have been editable (DD7).
  const gone = plan.rooms.filter((r) => !next.rooms.some((q) => q.id === r.id)).map((r) => r.id);
  const changed = changedRoomIds(plan, next);
  const blocked = [...changed, ...gone].find((id) => id in rooms && !rooms[id].editable);
  if (blocked) {
    history.discard();
    const name = findRoom(plan, blocked)?.name ?? 'another room';
    return { plan: null, res: { ok: false, message: `That would also change ${name}, which can only be edited on the phone app.` } };
  }
  for (const id of changed) {
    const problem = roomProblem(findRoom(next, id)!);
    if (problem) { history.discard(); return { plan: null, res: { ok: false, message: problem } }; }
  }
  return { plan: next, res };
}

/** A rectangle's two editable sides: Length = its first wall, Width = the next (stable across edits). */
export function rectSides(r: Room): { lengthVid: number; widthVid: number } | null {
  if (r.corners.length !== 4 || !asRectangle(r.corners)) return null;
  return { lengthVid: r.corners[0].id, widthVid: r.corners[1].id };
}

/** Current length of a straight wall, or null (diagonal or gone). */
export function wallFt(plan: Plan, rid: string, vid: number): number | null {
  const info = lengthInfo(plan, rid, vid);
  return 'ok' in info ? null : info.currentFt;
}

type MeasureOpts = { moveEnd?: boolean; chosen?: boolean };

/**
 * Set a wall's length as the phone's Wall length sheet does (`wallLength`). The phone answers a
 * "doesn't match the earlier measurement" conflict with Use new; on the web the typed value is
 * the user's correction of that very number, so it is applied as Use new at once (DD-W3.2) and
 * the note says which reading it replaced. Both walls held: the choices come back to the panel.
 */
export function setWallLength(
  plan: Plan, history: History, rooms: Record<string, RoomEditability>, rid: string, vid: number, valueFt: number, opts: MeasureOpts = {},
): { plan: Plan | null; result: EditResult } {
  const cur = wallFt(plan, rid, vid);
  if (cur != null && Math.abs(cur - valueFt) < 1e-6) return { plan: null, result: { ok: true } };
  const first = runEdit(plan, history, rooms, (p) => wallLength(p, rid, vid, valueFt, opts));
  if (first.plan) return { plan: first.plan, result: { ok: true } };
  const res = first.res as { ok: false; err?: string; message: string; cur?: number; options?: { label: string; moveQ: boolean }[] };
  if (res.err === 'conflict') {
    const again = runEdit(plan, history, rooms, (p) => wallLength(p, rid, vid, valueFt, { ...opts, useNew: true }));
    if (again.plan) return { plan: again.plan, result: { ok: true, note: `Replaced the earlier ${fmt(res.cur ?? cur ?? 0)} measurement.` } };
    return { plan: null, result: { ok: false, message: (again.res as { message: string }).message } };
  }
  if (res.err === 'same') return { plan: null, result: { ok: true } };
  if (res.err === 'choose-wall') {
    return { plan: null, result: { ok: false, message: res.message, options: (res.options ?? []).map((o) => ({ label: o.label, moveQ: o.moveQ })) } };
  }
  return { plan: null, result: { ok: false, message: res.message } };
}


/**
 * After a 200: the sent plan is what is stored now. Each saved room's base becomes the
 * response's `updated_at` and `geometry_version`, and its stored copy the document sent.
 */
export function afterSave(
  rooms: Record<string, RoomEditability>, sent: PlanRoomEdit[], saved: { room_id: string; updated_at?: string | null; geometry_version?: number | null }[],
): Record<string, RoomEditability> {
  const out = { ...rooms };
  for (const e of sent) {
    const cur: RoomEditability | undefined = out[e.room_id] ?? (e.create ? { editable: true, baseVersion: 0, baseUpdatedAt: null, stored: {} } : undefined);
    if (!cur?.editable) continue;
    const s = saved.find((x) => x.room_id === e.room_id);
    out[e.room_id] = {
      editable: true,
      baseVersion: s?.geometry_version ?? Math.max(cur.baseVersion, WEB_GEOMETRY_VERSION),
      baseUpdatedAt: s?.updated_at ?? cur.baseUpdatedAt,
      stored: { ...(e.geometry as unknown as Record<string, unknown>), geometryVersion: s?.geometry_version ?? Math.max(cur.baseVersion, WEB_GEOMETRY_VERSION) },
    };
  }
  return out;
}
